import React, { useState, useEffect, useCallback } from 'react';
import chemicalService from '../../../services/chemicalService';
import GatePassFormModal from '../components/GatePassFormModal';
import InwardFormModal from '../components/InwardFormModal';
import { generateGatePassPdf } from '../components/gatePassPdf';

const PAGE_SIZE = 10;

const GatePassTab = ({ lockDirection = null }) => {
  const [gatePasses, setGatePasses] = useState([]);
  const [summary, setSummary] = useState({
    outward_this_month: 0,
    inward_this_month: 0,
    in_transit: 0,
    returned_this_month: 0,
  });
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const [directionFilter, setDirectionFilter] = useState(lockDirection || 'outward'); // default to outward (most common)
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingGatePass, setEditingGatePass] = useState(null);
  const [showInwardModal, setShowInwardModal] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [directionFilter, statusFilter, debouncedSearch, fromDate, toDate]);

  const fetchGatePasses = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (directionFilter !== 'all') params.direction = directionFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const [listRes, summaryRes] = await Promise.all([
        chemicalService.getGatePasses(params),
        chemicalService.getGatePassesSummary().catch((e) => {
          console.warn('Gate passes summary not available:', e.message);
          return { data: { outward_this_month: 0, inward_this_month: 0, in_transit: 0, returned_this_month: 0 } };
        }),
      ]);

      setGatePasses(listRes.data || []);
      setPagination(listRes.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
      setSummary(summaryRes.data || { outward_this_month: 0, inward_this_month: 0, in_transit: 0, returned_this_month: 0 });
    } catch (err) {
      console.error('Error fetching gate passes:', err);
      alert('❌ ' + (err.response?.data?.message || 'Failed to load gate passes'));
    } finally {
      setLoading(false);
    }
  }, [page, directionFilter, statusFilter, debouncedSearch, fromDate, toDate]);

  useEffect(() => {
    fetchGatePasses();
  }, [fetchGatePasses]);

  // Add summary endpoint via service if missing
  // Use the chemicalService - we need getGatePassesSummary

  const handleEditClick = async (gp) => {
    try {
      const res = await chemicalService.getGatePassById(gp.id);
      setEditingGatePass(res.data);
      setShowFormModal(true);
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to load gate pass'));
    }
  };

  const handleDelete = async (gp) => {
    if (!window.confirm(`Delete gate pass "${gp.gp_no}"?\n\nThis cannot be undone.`)) return;
    try {
      await chemicalService.deleteGatePass(gp.id);
      alert('✅ Gate pass deleted.');
      if (gatePasses.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchGatePasses();
      }
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to delete gate pass'));
    }
  };

  const handleDownloadPdf = async (gp) => {
    try {
      // Fetch full gate pass with items (list view may not include items)
      const res = await chemicalService.getGatePassById(gp.id);
      generateGatePassPdf(res.data);
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to generate PDF'));
    }
  };

  const handleStatusChange = async (gp, newStatus) => {
    if (gp.status === newStatus) return;

    const statusLabels = { issued: 'Issued', delivered: 'Delivered', returned: 'Returned' };
    if (!window.confirm(`Change status of "${gp.gp_no}" from ${statusLabels[gp.status]} to ${statusLabels[newStatus]}?`)) {
      return;
    }
    try {
      await chemicalService.updateGatePassStatus(gp.id, newStatus);
      // Optimistic refresh
      fetchGatePasses();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to update status'));
    }
  };

  const handleSaved = () => {
    setShowFormModal(false);
    setEditingGatePass(null);
    fetchGatePasses();
  };

  const clearDateFilters = () => {
    setFromDate('');
    setToDate('');
  };

  return (
    <div style={{ padding: '24px 30px 40px' }}>

      {/* ─── Summary Cards ───────────────────────── */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #e74c3c' }}>
          <div style={styles.summaryLabel}>↑ Outward This Month</div>
          <div style={{ ...styles.summaryValue, color: '#e74c3c' }}>{summary.outward_this_month}</div>
          <div style={styles.summarySub}>Goods sent out</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #3498db' }}>
          <div style={styles.summaryLabel}>↓ Inward This Month</div>
          <div style={{ ...styles.summaryValue, color: '#3498db' }}>{summary.inward_this_month}</div>
          <div style={styles.summarySub}>Goods received</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #f39c12' }}>
          <div style={styles.summaryLabel}>In Transit</div>
          <div style={{ ...styles.summaryValue, color: '#f39c12' }}>{summary.in_transit}</div>
          <div style={styles.summarySub}>Pending delivery</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #95a5a6' }}>
          <div style={styles.summaryLabel}>Returned This Month</div>
          <div style={{ ...styles.summaryValue, color: '#95a5a6' }}>{summary.returned_this_month}</div>
          <div style={styles.summarySub}>Sent back</div>
        </div>
      </div>

      {/* ─── Direction + Status Filter Row ──────── */}
      <div style={styles.toolbar}>
        {!lockDirection && (
        <div style={styles.filterGroup}>
          <span style={styles.filterGroupLabel}>Direction:</span>
          <div style={styles.filterTabs}>
            {[
              { id: 'all', label: 'All' },
              { id: 'outward', label: '↑ Outward' },
              { id: 'inward', label: '↓ Inward' },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setDirectionFilter(f.id)}
                style={{
                  ...styles.filterButton,
                  background: directionFilter === f.id ? '#e74c3c' : 'white',
                  color: directionFilter === f.id ? 'white' : '#2c3e50',
                  borderColor: directionFilter === f.id ? '#e74c3c' : '#e0e0e0',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        )}
        <div style={styles.filterGroup}>
          <span style={styles.filterGroupLabel}>Status:</span>
          <div style={styles.filterTabs}>
            {[
              { id: 'all', label: 'All' },
              { id: 'issued', label: '▶ Issued' },
              { id: 'delivered', label: '✓ Delivered' },
              { id: 'returned', label: '↩ Returned' },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setStatusFilter(f.id)}
                style={{
                  ...styles.filterButton,
                  background: statusFilter === f.id ? '#e74c3c' : 'white',
                  color: statusFilter === f.id ? 'white' : '#2c3e50',
                  borderColor: statusFilter === f.id ? '#e74c3c' : '#e0e0e0',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div style={styles.dateRangeWrap}>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={styles.dateInput}
          />
          <span style={{ color: '#95a5a6', fontSize: '13px' }}>→</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={styles.dateInput}
          />
          {(fromDate || toDate) && (
            <button onClick={clearDateFilters} style={styles.clearButton}>✕</button>
          )}
        </div>
      </div>

      <div style={styles.toolbarBottom}>
        <div style={styles.searchWrap}>
          <span style={styles.searchIcon}>⌕</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search GP no, rider, or vehicle..."
            style={styles.searchInput}
          />
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => setShowInwardModal(true)} style={styles.inwardButton}>
            ↓ Inward (purchase)
          </button>
          <button onClick={() => { setEditingGatePass(null); setShowFormModal(true); }} style={styles.addButton}>
            ↑ Outward
          </button>
        </div>
      </div>

      {/* ─── Gate Pass Table ─────────────────────── */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>GP No</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Direction</th>
              <th style={styles.th}>Party</th>
              <th style={styles.th}>Rider / Vehicle</th>
              <th style={styles.th}>Status</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" style={styles.noData}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                  Loading gate passes...
                </td>
              </tr>
            ) : gatePasses.length === 0 ? (
              <tr>
                <td colSpan="7" style={styles.noData}>
                  <div style={{ fontSize: '40px', marginBottom: '8px' }}>🚚</div>
                  {debouncedSearch || statusFilter !== 'all' || fromDate || toDate
                    ? 'No gate passes match your filters'
                    : 'No gate passes yet — click "+ New gate pass" to record one'}
                </td>
              </tr>
            ) : (
              gatePasses.map((gp) => (
                <tr key={gp.id} style={styles.row}>
                  <td style={styles.td}>
                    <div style={{ fontWeight: '700', color: '#2c3e50', fontFamily: 'monospace' }}>
                      {gp.gp_no}
                    </div>
                  </td>
                  <td style={styles.td}>{formatDate(gp.gp_date)}</td>
                  <td style={styles.td}>
                    <span style={gp.direction === 'outward' ? styles.outwardBadge : styles.inwardBadge}>
                      {gp.direction === 'outward' ? '↑ Outward' : '↓ Inward'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontWeight: '600', color: '#2c3e50' }}>{gp.party_name || '—'}</div>
                    <div style={{ fontSize: '11px', color: '#95a5a6', marginTop: '2px' }}>
                      {gp.party_type === 'customer' ? 'Customer' : 'Vendor'}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: '13px', color: '#2c3e50' }}>👤 {gp.rider_name}</div>
                    <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '2px', fontFamily: 'monospace' }}>
                      🚛 {gp.vehicle_no}
                    </div>
                  </td>
                  <td style={styles.td}>
                    {gp.direction === 'inward' ? (
                      <span style={{ ...styles.statusDropdown, ...getStatusStyle('delivered'), display: 'inline-block', cursor: 'default' }}>
                        ✓ Delivered
                      </span>
                    ) : (
                      <select
                        value={gp.status}
                        onChange={(e) => handleStatusChange(gp, e.target.value)}
                        style={{
                          ...styles.statusDropdown,
                          ...getStatusStyle(gp.status),
                        }}
                      >
                        <option value="issued">▶ Issued</option>
                        <option value="delivered">✓ Delivered</option>
                        <option value="returned">↩ Returned</option>
                      </select>
                    )}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                  <div style={{ display: 'inline-flex', gap: '6px' }}>
                      {gp.direction === 'outward' && (
                        <button onClick={() => handleEditClick(gp)} style={styles.editButton} title="Edit">
                          ✏️
                        </button>
                      )}
                      <button onClick={() => handleDelete(gp)} style={styles.deleteButton} title="Delete">
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Pagination ──────────────────────────── */}
      {!loading && pagination.total > 0 && (
        <div style={styles.paginationWrap}>
          <div style={styles.paginationInfo}>
            Showing {(pagination.page - 1) * pagination.limit + 1}
            –{Math.min(pagination.page * pagination.limit, pagination.total)}
            {' '}of {pagination.total} gate passes
          </div>
          <div style={styles.paginationButtons}>
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              «
            </button>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              ‹ Prev
            </button>
            <span style={styles.pageIndicator}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              Next ›
            </button>
            <button
              onClick={() => setPage(pagination.totalPages)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* ─── Form Modal ──────────────────────────── */}
      {showFormModal && (
        <GatePassFormModal
          gatePass={editingGatePass}
          onClose={() => { setShowFormModal(false); setEditingGatePass(null); }}
          onSaved={handleSaved}
        />
      )}
      {showInwardModal && (
        <InwardFormModal
          onClose={() => setShowInwardModal(false)}
          onSaved={() => { setShowInwardModal(false); fetchGatePasses(); }}
        />
      )}

    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getStatusStyle = (status) => {
  const palette = {
    issued:    { bg: '#fff3cd', fg: '#856404', border: '#f9e0a0' },
    delivered: { bg: '#d4edda', fg: '#155724', border: '#b2dfb6' },
    returned:  { bg: '#f8d7da', fg: '#721c24', border: '#e8b5b9' },
  };
  const c = palette[status] || palette.issued;
  return {
    background: c.bg,
    color: c.fg,
    borderColor: c.border,
  };
};

// ─── Styles ───────────────────────────────────────────────

const styles = {
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  summaryCard: {
    background: 'white',
    padding: '18px 22px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  summaryLabel: { fontSize: '13px', color: '#7f8c8d', marginBottom: '6px', fontWeight: '600' },
  summaryValue: { fontSize: '28px', fontWeight: '700' },
  summarySub: { fontSize: '11px', color: '#95a5a6', marginTop: '4px' },

  toolbar: {
    background: 'white',
    padding: '16px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', flexWrap: 'wrap', gap: '20px',
    alignItems: 'center',
    marginBottom: '12px',
  },
  toolbarBottom: {
    background: 'white',
    padding: '14px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', flexWrap: 'wrap', gap: '12px',
    justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '16px',
  },

  filterGroup: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  filterGroupLabel: {
    fontSize: '12px', fontWeight: '700',
    color: '#7f8c8d', textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  filterTabs: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  filterButton: {
    padding: '6px 12px',
    border: '2px solid', borderRadius: '7px',
    fontSize: '12px', fontWeight: '600',
    cursor: 'pointer', transition: 'all 0.2s',
  },

  dateRangeWrap: {
    display: 'flex', alignItems: 'center', gap: '6px',
  },
  dateInput: {
    padding: '7px 10px',
    border: '2px solid #e0e0e0', borderRadius: '7px',
    fontSize: '12px', fontFamily: 'inherit',
  },
  clearButton: {
    padding: '4px 10px',
    background: '#f0f0f0', color: '#7f8c8d',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '700',
  },

  searchWrap: { position: 'relative' },
  searchIcon: {
    position: 'absolute', left: '12px', top: '50%',
    transform: 'translateY(-50%)',
    color: '#95a5a6', fontSize: '16px',
  },
  searchInput: {
    padding: '10px 12px 10px 36px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', width: '280px',
    fontFamily: 'inherit',
  },
  addButton: {
    padding: '10px 20px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
  inwardButton: {
    padding: '10px 20px',
    background: '#3498db', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },

  tableWrap: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#f8f9fa' },
  th: {
    padding: '14px 16px', textAlign: 'left',
    fontSize: '13px', fontWeight: '700',
    color: '#2c3e50', borderBottom: '2px solid #e0e0e0',
    whiteSpace: 'nowrap',
  },
  row: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#2c3e50' },
  noData: {
    padding: '60px 20px', textAlign: 'center',
    color: '#95a5a6', fontSize: '14px',
  },

  outwardBadge: {
    background: '#fdecea', color: '#721c24',
    padding: '4px 12px', borderRadius: '12px',
    fontSize: '12px', fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  inwardBadge: {
    background: '#cce5ff', color: '#004085',
    padding: '4px 12px', borderRadius: '12px',
    fontSize: '12px', fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  statusDropdown: {
    padding: '6px 10px',
    border: '2px solid',
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: 'inherit',
    minWidth: '130px',
  },

  editButton: {
    padding: '6px 10px',
    background: '#3498db', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },
  pdfButton: {
    padding: '6px 10px',
    background: '#27ae60', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },
  deleteButton: {
    padding: '6px 10px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },

  paginationWrap: {
    marginTop: '16px',
    background: 'white',
    padding: '14px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', flexWrap: 'wrap', gap: '12px',
  },
  paginationInfo: { fontSize: '13px', color: '#7f8c8d' },
  paginationButtons: { display: 'flex', gap: '6px', alignItems: 'center' },
  pageButton: {
    padding: '8px 14px',
    background: 'white', color: '#2c3e50',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '600',
  },
  pageButtonDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  pageIndicator: {
    padding: '0 12px', fontSize: '13px',
    color: '#2c3e50', fontWeight: '600',
  },
};

export default GatePassTab;